import React, { useState } from 'react';
import { userApiClient } from '../../api/HttpClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Layers, DoorOpen } from 'lucide-react';

interface CreateRoomDialogProps {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName?: string;
  onRoomCreated: () => void;
}

type Mode = 'floor' | 'single';

const CreateRoomDialog: React.FC<CreateRoomDialogProps> = ({
  open, onClose, propertyId, propertyName = '', onRoomCreated,
}) => {
  const [mode, setMode]               = useState<Mode>('floor');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);

  // Floor mode
  const [floorName, setFloorName]     = useState('');
  const [roomCount, setRoomCount]     = useState('');
  const [bedsPerRoom, setBedsPerRoom] = useState('');

  // Single mode
  const [roomNumber, setRoomNumber]   = useState('');
  const [capacity, setCapacity]       = useState('');

  if (!open) return null;

  const preview = floorName && Number(roomCount) > 0
    ? Array.from({ length: Math.min(Number(roomCount), 5) }, (_, i) => `${floorName}${String(i + 1).padStart(2, '0')}`)
    : [];

  const handleFloorSubmit = async () => {
    setError(null);
    if (!floorName || !roomCount || !bedsPerRoom) { setError('All fields are required.'); return; }
    const rc = Number(roomCount), bpr = Number(bedsPerRoom);
    if (rc < 1 || rc > 50)  { setError('Room count must be 1–50.'); return; }
    if (bpr < 1 || bpr > 20) { setError('Beds per room must be 1–20.'); return; }

    setLoading(true);
    try {
      const client = await userApiClient;
      const data = await client.post<{ created: number; skipped: number; skipped_rooms: string[] }>(
        `/api/pg_tenant/property/${propertyId}/bulk-create-rooms/`,
        { floor_name: floorName, room_count: rc, beds_per_room: bpr, property_name: propertyName }
      );
      const msg = data.skipped > 0
        ? `Created ${data.created} rooms. Skipped ${data.skipped} (already exist).`
        : `Created ${data.created} rooms successfully.`;
      setSuccess(msg);
      onRoomCreated();
      setTimeout(() => { setSuccess(null); onClose(); setFloorName(''); setRoomCount(''); setBedsPerRoom(''); }, 2000);
    } catch (e: any) {
      setError(e.message || 'Failed to create rooms.');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleSubmit = async () => {
    setError(null);
    if (!roomNumber || !capacity) { setError('Room number and capacity are required.'); return; }
    if (Number(capacity) < 1 || Number(capacity) > 20) { setError('Beds per room must be 1–20.'); return; }

    setLoading(true);
    try {
      const client = await userApiClient;
      await client.post(`/api/pg_tenant/longterm/rooms/`, {
        property_id: propertyId, room_number: roomNumber, capacity: Number(capacity),
      });
      onRoomCreated();
      onClose();
      setRoomNumber(''); setCapacity('');
    } catch (e: any) {
      setError(e.message || 'Failed to create room.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-primary px-5 py-4">
          <h2 className="text-primary-foreground font-bold text-lg">Add Rooms</h2>
          <p className="text-primary-foreground/70 text-sm">Create rooms one by one or an entire floor at once</p>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-border">
          {(['floor', 'single'] as Mode[]).map((m) => (
            <button key={m}
              onClick={() => { setMode(m); setError(null); setSuccess(null); }}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors',
                mode === m ? 'bg-primary/5 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:bg-muted/50'
              )}>
              {m === 'floor' ? <><Layers className="h-4 w-4" />Add Full Floor</> : <><DoorOpen className="h-4 w-4" />Single Room</>}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {mode === 'floor' ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Floor Name *</Label>
                  <Input placeholder="e.g. 0, 1, 2, 3..." value={floorName} maxLength={5}
                    onChange={e => setFloorName(e.target.value)} className="bg-background" />
                  <p className="text-xs text-muted-foreground">Prefix · max 5 chars</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">No. of Rooms *</Label>
                  <Input placeholder="1–50" type="number" min={1} max={50} value={roomCount}
                    onChange={e => setRoomCount(e.target.value)} className="bg-background" />
                  <p className="text-xs text-muted-foreground">Max 50 rooms</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Beds / Room *</Label>
                  <Input placeholder="1–20" type="number" min={1} max={20} value={bedsPerRoom}
                    onChange={e => setBedsPerRoom(e.target.value)} className="bg-background" />
                  <p className="text-xs text-muted-foreground">Max 20 beds</p>
                </div>
              </div>

              {/* Preview */}
              {preview.length > 0 && (
                <div className="bg-muted/40 border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Room names preview:</p>
                  <div className="flex flex-wrap gap-2">
                    {preview.map(r => (
                      <span key={r} className="bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-md">{r}</span>
                    ))}
                    {Number(roomCount) > 5 && (
                      <span className="text-xs text-muted-foreground self-center">+{Number(roomCount) - 5} more</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Total: <b>{Number(roomCount)} rooms</b> × <b>{bedsPerRoom} beds</b> = <b>{Number(roomCount) * Number(bedsPerRoom)} beds</b>
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Room Number *</Label>
                <Input placeholder="e.g. 001, 101, 201" value={roomNumber}
                  onChange={e => setRoomNumber(e.target.value)} className="bg-background" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Beds / Room *</Label>
                <Input placeholder="1–20" type="number" min={1} max={20} value={capacity}
                  onChange={e => setCapacity(e.target.value)} className="bg-background" />
                <p className="text-xs text-muted-foreground">Max 20 beds</p>
              </div>
            </>
          )}

          {error   && <p className="text-sm text-red-500">{error}</p>}
          {success && <p className="text-sm text-primary font-medium">✓ {success}</p>}
        </div>

        <div className="px-5 pb-5 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={mode === 'floor' ? handleFloorSubmit : handleSingleSubmit}
            disabled={loading}>
            {loading ? 'Creating...' : mode === 'floor' ? `Create ${roomCount || 0} Rooms` : 'Create Room'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateRoomDialog;
